<script setup>
import { ref, onMounted, computed } from "vue";
import axios from 'axios';
import { useLoading } from 'vue-loading-overlay'
import Layout from "@/layouts/mainfw.vue";
import PageHeader from "@/components/page-header";
import { useFastWay } from "@/state/modules/fastway";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-balham.css";
import { AgGridVue } from "ag-grid-vue3";
import Dialog from '@/views/components/Dialog.vue';
import {DropDownListComponent} from "@syncfusion/ej2-vue-dropdowns";
import { Toast } from 'bootstrap';

const store = useFastWay();
const loader = useLoading();
let dialog = ref(null);
let loaded = ref(false);
let projects = ref([]);
let data = ref([]);
let totalRowCount = ref(0);
let isProjectManager = ref(false);
const title = "Indicadores";
const items = [
  {
    text: "Tablero",
    href: "/",
  },
  {
    text: "Indicadores",
    active: true,
  },
];

const api = computed({
    get() {
      return store.parameters.apiURL;
    }
});

const viewParms = computed({
  get() {
    return store.viewParameters.projectMetrics;
  }
});

const formatNumber = params => {
  let value = '';
  if (typeof params.value != 'undefined') {
    if (params.value != 0) {
      value = params.value.toLocaleString('es-GT', {minimumFractionDigits: 0, maximumFractionDigits: 0});
    }
  }
  return value;
}

const formatPercentage = params => {
  let value = '';
  if (typeof params.value != 'undefined') {
    if (params.value != 0) {
      value = params.value.toLocaleString('es-GT', {minimumFractionDigits: 2, maximumFractionDigits: 2}) + '%';
    }
  }
  return value;
}

const getTextColorForBackground = hexColor => {
  if (!hexColor || typeof hexColor !== 'string') return 'black';
  const hex = hexColor.replace('#', '');
  if (hex.length !== 6 || !/^[0-9a-fA-F]{6}$/.test(hex)) return 'black';
  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);
  const luminance = 0.299 * r + 0.587 * g + 0.114 * b;
  return luminance > 128 ? 'black' : 'white';
}

const getCellStyle = params => {
  const bgColor = params.data.color || '#ffffff';
  const textColor = getTextColorForBackground(params.data.color);
  return {'backgroundColor': bgColor, 'color': textColor, 'textAlign': 'right'};
}

const monthColumn = (headerName, monthNumber) => {
  const suffix = monthNumber.toString().padStart(2, '0');
  return {
    headerName,
    children: [
      { headerName: 'Logros', field: `execution${suffix}`, width: 100, cellStyle: getCellStyle, valueFormatter: formatNumber, editable: () => isProjectManager.value, cellEditor: 'agNumberCellEditor' },
      { headerName: 'Meta', field: `goal${suffix}`, width: 100, cellStyle: getCellStyle, valueFormatter: formatNumber, editable: () => isProjectManager.value },
      { headerName: '%', field: `percentage${suffix}`, width: 90, cellStyle: getCellStyle, valueFormatter: formatPercentage }
    ]
  };
};

const columnDefs = computed(() => [
  {
    headerName: 'Área De Evaluación',
    field: 'evaluationAreaDescription',
    autoHeight: true,
    width: 180,
    sortable: false,
    pinned: 'left',
    cellStyle: params => {
      const bgColor = params.data.color || '#ffffff';
      const textColor = getTextColorForBackground(params.data.color);
      return {'white-space': 'normal', 'backgroundColor': bgColor, 'color': textColor};
    }
  },
  {
    headerName: 'Indicador De Cambio Esperado',
    field: 'description',
    autoHeight: true,
    width: 400,
    sortable: false,
    pinned: 'left',
    cellRenderer: params => {
      let rend = '';
      if (params.data.level <= 1) {
        rend = `<h5>${params.data.description}</h5>`;
      } else if (params.data.level == 2) {
        rend = `<b style="margin-left: 10px;">${params.data.description}</b>`;
      } else if (params.data.level == 3) {
        rend = `<div style="margin-left: 20px;">${params.data.description}</div>`;
      } else {
        rend = `<div style="margin-left: 30px;">${params.data.description}</div>`;
      }
      return rend;
    },
    cellStyle: params => {
      const bgColor = params.data.color || '#ffffff';
      const textColor = getTextColorForBackground(params.data.color);
      return {'white-space': 'normal', 'backgroundColor': bgColor, 'color': textColor};
    }
  },
  { headerName: 'TOTAL', children: [
      { headerName: 'Logros', field: 'executionTotal', width: 100, cellStyle: getCellStyle, valueFormatter: formatNumber },
      { headerName: 'Meta', field: 'goalTotal', width: 100, cellStyle: getCellStyle, valueFormatter: formatNumber },
      { headerName: '%', field: 'percentageTotal', width: 90, cellStyle: getCellStyle, valueFormatter: formatPercentage }
    ]
  },
  monthColumn('Enero', 1),
  monthColumn('Febrero', 2),
  monthColumn('Marzo', 3),
  monthColumn('Abril', 4),
  monthColumn('Mayo', 5),
  monthColumn('Junio', 6),
  monthColumn('Julio', 7),
  monthColumn('Agosto', 8),
  monthColumn('Septiembre', 9),
  monthColumn('Octubre', 10),
  monthColumn('Noviembre', 11),
  monthColumn('Diciembre', 12)
]);

onMounted(async () => {
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando catálogos'});
  const params = {
    userId: store.userInfo.userId,
    token: store.userInfo.token
  };
  await axios
  .post(`${api.value}/sbg/projects/user_list`, params)
  .then(response => {
    if (response.data.success) {
      projects.value = response.data.data;
    } else {
      dialog.value.show('error', 'Error al cargar proyectos del usuario', response.data.message);
    }
    getData();
    loading.hide();
  })
  .catch(error => {
      let errorMessage = '';
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response) {
        errorMessage = error.response.data.ErrorMessage;
      } else {
        errorMessage = 'Error de conectividad al cargar catálogo';
      }
      loading.hide();
      dialog.value.show('error', 'Error al cargar catálogos', errorMessage);
  });
});

const checkProjectManager = async () => {
  isProjectManager.value = false;
  if (!viewParms.value.projectId) {
    return;
  }
  await axios
  .post(`${api.value}/sbg/projects/data`, {id: viewParms.value.projectId, context: store.userInfo})
  .then(response => {
    if (response.data.success && response.data.data) {
      const managers = response.data.data.managers || [];
      isProjectManager.value = managers.includes(store.userInfo.userId);
    }
  })
  .catch(() => {
    isProjectManager.value = false;
  });
}

const getData = async () => {
  data.value = [];
  totalRowCount.value = 0;
  await checkProjectManager();
  const loading = loader.show({loader: 'bars', color: '#0000FF'}, {after: () => 'Cargando métricas de proyecto'});
  const params = {
    userId: store.userInfo.userId,
    token: store.userInfo.token,
    year: viewParms.value.year,
    projectId: viewParms.value.projectId
  };
  await axios
  .post(`${api.value}/sbg/projects/metrics/list`, params)
  .then(response => {
    if (response.data.success) {
      if (response.data.data) {
        data.value = response.data.data;
        if (response.data.data.length > 0) {
          totalRowCount.value = response.data.data[0].totalRowCount;
        }
      }
    } else {
      dialog.value.show('error', 'Error interno al cargar métricas de proyecto', response.data.message);
    }
    loading.hide();
    loaded.value = true;
  })
  .catch(error => {
    loaded.value = true;
    loading.hide();
    dialog.value.show('error', 'Error al cargar métricas de proyecto', error.message);
  });
}

const onFilterChange = () => {
  viewParms.value.currentPage = 1;
  getData();
}

const showToast = (message, variant = 'success') => {
  const bgClass = variant === 'success' ? 'bg-success' : 'bg-danger';
  const container = document.getElementById('toast-container');
  if (!container) return;
  const toastHtml = `
    <div class="toast align-items-center ${bgClass} text-white border-0" role="alert" aria-live="assertive" aria-atomic="true">
      <div class="d-flex">
        <div class="toast-body">${message}</div>
        <button type="button" class="btn-close btn-close-white me-2 m-auto" data-bs-dismiss="toast" aria-label="Close"></button>
      </div>
    </div>
  `;
  container.insertAdjacentHTML('beforeend', toastHtml);
  const toastElement = container.lastElementChild;
  const bsToast = new Toast(toastElement, { delay: 3000 });
  bsToast.show();
  toastElement.addEventListener('hidden.bs.toast', () => {
    toastElement.remove();
  });
}

const goalMonthMap = {
  'goal01': 1, 'goal02': 2, 'goal03': 3, 'goal04': 4,
  'goal05': 5, 'goal06': 6, 'goal07': 7, 'goal08': 8,
  'goal09': 9, 'goal10': 10, 'goal11': 11, 'goal12': 12
};

const executionMonthMap = {
  'execution01': 1, 'execution02': 2, 'execution03': 3, 'execution04': 4,
  'execution05': 5, 'execution06': 6, 'execution07': 7, 'execution08': 8,
  'execution09': 9, 'execution10': 10, 'execution11': 11, 'execution12': 12
};

const saveGoal = async (params) => {
  const month = goalMonthMap[params.column.colDef.field];
  if (!month || !isProjectManager.value) {
    getData();
    return;
  }
  
  const payload = {
    context: {
      userId: store.userInfo.userId,
      token: store.userInfo.token
    },
    id: 0,
    projectId: viewParms.value.projectId,
    activityId: params.data.activityId,
    year: viewParms.value.year,
    month: month,
    goal: params.newValue
  };
  
  try {
    const response = await axios.post(`${api.value}/sbg/projects/metrics/save_goal`, payload);
    if (response.data.success) {
      showToast('Meta actualizada correctamente', 'success');
      getData();
    } else {
      dialog.value.show('error', 'Error al actualizar meta', response.data.message);
    }
  } catch (error) {
    let errorMessage = 'Error de conectividad';
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    dialog.value.show('error', 'Error al actualizar meta', errorMessage);
  }
}

const saveExecution = async (params) => {
  const month = executionMonthMap[params.column.colDef.field];
  if (!month || !isProjectManager.value) {
    getData();
    return;
  }

  const payload = {
    context: {
      userId: store.userInfo.userId,
      token: store.userInfo.token
    },
    projectId: viewParms.value.projectId,
    activityId: params.data.activityId,
    year: viewParms.value.year,
    month: month,
    execution: params.newValue
  };

  if (typeof params.data.comments !== 'undefined') {
    payload.comments = params.data.comments;
  }

  try {
    const response = await axios.post(`${api.value}/sbg/projects/metrics/save_execution`, payload);
    if (response.data.success) {
      showToast('Logro actualizado correctamente', 'success');
      getData();
    } else {
      dialog.value.show('error', 'Error al actualizar logro', response.data.message);
    }
  } catch (error) {
    let errorMessage = 'Error de conectividad';
    if (error.response && error.response.data && error.response.data.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    dialog.value.show('error', 'Error al actualizar logro', errorMessage);
  }
}

const onCellValueChanged = (params) => {
  if (goalMonthMap[params.column.colDef.field]) {
    saveGoal(params);
  } else if (executionMonthMap[params.column.colDef.field]) {
    saveExecution(params);
  }
}

const years = computed(() => {
  const currentYear = new Date().getFullYear() + 1;
  return Array.from({ length: 6 }, (_, i) => currentYear - i);
});

const onYearChange = () => {
  getData();
}

</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
     <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-body">
                    <div class="d-flex justify-content-between align-items-center align-middle row mb-2">
                        <div class="d-inline-block mr-1" style="width: 400px;">
                          <DropDownListComponent v-model:value="viewParms.projectId" floatLabelType="Auto" placeholder="Proyecto" highlight=true :allowFiltering="true" filterType="Contains" :dataSource="projects" :fields="{text: 'name', value: 'id'}" :showClearButton='false' :change="onFilterChange"/>
                        </div>
                        <div class="col-1">
                          <DropDownListComponent v-model:value="viewParms.year" floatLabelType="Auto" placeholder="Año" :dataSource="years" :change="onYearChange"/>
                        </div>
                        <div class="col d-flex justify-content-start">
                          <b-button variant="outline-success" class="btn-icon waves-effect waves-light me-1" v-b-tooltip.hover title="Recargar" @click="getData">
                            <i class="las la-sync"></i>
                          </b-button>
                        </div>
                    </div>
                    <ag-grid-vue
                        style="width: 100%; height: 700px;"
                        class="ag-theme-balham"
                        :columnDefs="columnDefs"
                        :rowData="data"
                        :accentedSort="true"
                        @cell-value-changed="onCellValueChanged"
                    >
                    </ag-grid-vue>
                </div>
            </div>
        </div>
    </div>
    <Dialog ref="dialog"/>
    <div id="toast-container" class="toast-container position-fixed bottom-0 end-0 p-3" style="z-index: 9999;"></div>
  </Layout>
</template>

<style scoped>
@import "../../../../node_modules/@syncfusion/ej2-base/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-inputs/styles/material.css";
@import "../../../../node_modules/@syncfusion/ej2-vue-dropdowns/styles/material.css";

#input-container .e-input-group {
  margin: 30px 0;
}

.e-input-group-icon:before {
  font-family: e-icons;
}

.e-input-group .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group.e-small .e-input-group-icon.e-input-clear .e-input-group-icon.e-input-search {
  font-size: 12px;
}

.e-input-group-icon.e-input-clear:before {
  content: "\e7a7";
}

.e-input-group-icon.e-input-search:before {
  content: "\e993";
}

.number-cell {
  text-align: right;
}

:deep(.ag-theme-balham .ag-input-field-input) {
  color: black !important;
  background-color: white !important;
}
</style>